import { Button } from "@/components/ui/button";
import { BookOpen, ShoppingCart, Sparkles, CheckCircle2 } from "lucide-react";
import { BOOK_COVER, BOOK_ORDER_URL } from "@/lib/brand";

const questions = [
  "What exactly did they do differently to rise so fast?",
  "Where did my own career growth silently go wrong?",
  "How do I break out of years of stagnation — starting this month?",
  "What are the real habits behind their wealth, health, and success?",
];

export default function BookPromo() {
  return (
    <section
      id="book"
      className="relative py-20 md:py-32 px-6 md:px-10 overflow-hidden"
      data-testid="book-section"
    >
      <div className="absolute inset-0">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#2563EB]/10 blur-[140px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-[#38BDF8]/10 blur-[120px]" />
      </div>

      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Book cover */}
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative mx-auto lg:mx-0 w-full max-w-xl" data-testid="book-cover-wrapper">
            <div className="absolute -inset-8 bg-gradient-to-br from-[#2563EB]/30 to-[#38BDF8]/20 blur-3xl rounded-full" />
            <img
              src={BOOK_COVER}
              alt="Riding the Success Wave — A Blueprint for IT Professionals by Rahul Ganjare"
              className="relative w-full h-auto object-contain drop-shadow-[0_25px_60px_rgba(37,99,235,0.45)] hover:scale-[1.03] transition-transform duration-500"
              data-testid="book-cover-image"
            />

            <div className="absolute top-4 right-4 md:top-6 md:right-6 bg-[#38BDF8] text-[#0A1628] rounded-full w-20 h-20 md:w-24 md:h-24 flex flex-col items-center justify-center font-display font-bold shadow-lg rotate-12">
              <span className="text-[10px] md:text-xs uppercase tracking-wider">Save</span>
              <span className="text-2xl md:text-3xl leading-none">25%</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-7 order-1 lg:order-2">
          <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-[#38BDF8]">
            <BookOpen className="w-3.5 h-3.5" />
            New Book · Pre-Order Open
          </div>

          <h2 className="font-display text-4xl md:text-5xl font-normal text-white mt-4 leading-tight">
            <span className="text-[#38BDF8]">Riding the Success Wave.</span>
            <br />
            <span className="text-slate-300">A blueprint for IT professionals</span>
            <br />
            <span className="text-slate-500">to sky-rocket their career.</span>
          </h2>

          <p className="mt-6 text-lg text-slate-400 leading-relaxed max-w-2xl">
            Ever wondered how some colleagues rise so fast — while you stay stuck in
            the same role, same salary, same frustration? You both started the same
            degree, the same company, maybe even the same team. Yet years later, they
            lead, earn, and thrive.
            <span className="text-white font-medium"> The difference isn't luck. It's a playbook.</span>
          </p>

          <div className="mt-8 bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <div className="text-sm font-semibold text-white mb-4">
              If these questions dog you — this book is for you:
            </div>
            <ul className="space-y-3">
              {questions.map((q, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3"
                  data-testid={`book-question-${i}`}
                >
                  <CheckCircle2 className="w-5 h-5 text-[#38BDF8] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300">{q}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="mt-6 text-slate-400 leading-relaxed">
            Written by <span className="text-white font-semibold">Rahul Ganjare</span>,
            this book gives you the exact frameworks, habits, and shortcuts that separate
            the top 5% of IT professionals from everyone else. Coming soon on Amazon —
            <span className="text-[#38BDF8] font-semibold"> pre-order now and save 25%.</span>
          </p>

          {/* Pricing + CTA */}
          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="flex items-baseline gap-3" data-testid="book-pricing">
              <span className="text-slate-500 line-through text-lg">₹699</span>
              <span className="font-display text-4xl font-normal text-white">₹525</span>
              <span className="text-xs text-[#38BDF8] font-semibold uppercase tracking-wider bg-[#38BDF8]/10 px-2 py-1 rounded-full">
                Save ₹174
              </span>
            </div>

            <a
              href={BOOK_ORDER_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="book-order-cta"
            >
              <Button className="btn-yellow h-14 px-8 rounded-full font-bold uppercase tracking-wider text-sm">
                <ShoppingCart className="w-4 h-4 mr-2" />
                Order Now · Pre-Launch Price
              </Button>
            </a>
          </div>

          <div className="mt-6 flex items-center gap-2 text-xs text-slate-500">
            <Sparkles className="w-3.5 h-3.5 text-[#38BDF8]" />
            Secure checkout via Razorpay · Ships within India on launch
          </div>
        </div>
      </div>
    </section>
  );
}
